const ALLOWED_TAGS = new Set([
  'P', 'BR', 'STRONG', 'B', 'EM', 'I', 'U', 'S', 'STRIKE',
  'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'UL', 'OL', 'LI',
  'A', 'IMG', 'MARK', 'BLOCKQUOTE', 'PRE', 'CODE', 'TABLE', 'THEAD',
  'TBODY', 'TR', 'TH', 'TD', 'DIV', 'SPAN', 'HR',
]);
const ALLOWED_ATTRIBUTES = new Set(['href', 'src', 'alt', 'title', 'target', 'rel', 'width', 'height', 'colspan', 'rowspan', 'class', 'style', 'data-mention-email', 'data-mention-user-id', 'contenteditable']);

// Tiptap's Color/Highlight/TextStyle extensions emit inline `style="color: ...; background-color: ...; ..."`.
// Restrict to a small, non-executable declaration allowlist rather than passing style through verbatim.
const ALLOWED_STYLE_PROPERTIES = new Set([
  'color', 'background-color', 'font-weight', 'text-align', 'text-decoration',
  'border', 'border-collapse', 'border-spacing', 'padding', 'padding-top',
  'padding-right', 'padding-bottom', 'padding-left', 'max-width',
  'width', 'height', 'box-sizing', 'display', 'table-layout',
  'overflow-wrap', 'word-break', 'white-space', 'vertical-align', 'font-size', 'line-height',
]);

function sanitizeStyleValue(value: string): string {
  const safeDeclarations: string[] = [];
  for (const declaration of value.split(';')) {
    const [rawProperty, ...rawValueParts] = declaration.split(':');
    const property = rawProperty?.trim().toLowerCase();
    const propertyValue = rawValueParts.join(':').trim();
    if (!property || !propertyValue || !ALLOWED_STYLE_PROPERTIES.has(property)) continue;
    // Reject anything that could smuggle a URL/expression/behavior through a color value.
    if (/url\(|expression\(|javascript:|\/\*/i.test(propertyValue)) continue;
    if (!/^[#a-z0-9\s,.%()-]+$/i.test(propertyValue)) continue;
    safeDeclarations.push(`${property}: ${propertyValue}`);
  }
  return safeDeclarations.join('; ');
}

function safeUrl(value: string, attribute: 'href' | 'src'): boolean {
  const normalized = value.trim().toLowerCase();
  if (normalized.startsWith('javascript:') || normalized.startsWith('vbscript:') || normalized.startsWith('data:')) return false;
  if (attribute === 'src') return normalized.startsWith('https://') || normalized.startsWith('http://') || normalized.startsWith('/') || !normalized.includes(':');
  return normalized.startsWith('https://') || normalized.startsWith('http://') || normalized.startsWith('mailto:') || normalized.startsWith('/') || normalized.startsWith('#') || !normalized.includes(':');
}

function resolveInlineImageUrl(value: string): string {
  const inlinePath = '/api/inline-images/';
  if (!value.startsWith(inlinePath)) return value;

  const apiBase = (import.meta.env.VITE_API_BASE_URL || '/api').replace(/\/+$/, '');
  return apiBase === '/api' || apiBase.endsWith('/api')
    ? `${apiBase}${value.slice('/api'.length)}`
    : `${apiBase}${value}`;
}

/**
 * DOMParser-based allow-list sanitizer. Server-side DOMPurify remains the
 * persistence boundary; this is a second boundary before browser rendering.
 */
export function sanitizeHtml(value: string | null | undefined): string {
  if (!value || typeof document === 'undefined') return '';
  const documentFragment = new DOMParser().parseFromString(value, 'text/html');
  const output = document.createElement('div');

  const appendSafe = (node: Node, parent: HTMLElement) => {
    if (node.nodeType === Node.TEXT_NODE) {
      parent.appendChild(document.createTextNode(node.textContent || ''));
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const element = node as HTMLElement;
    if (!ALLOWED_TAGS.has(element.tagName)) {
      for (const child of Array.from(element.childNodes)) appendSafe(child, parent);
      return;
    }
    const clean = document.createElement(element.tagName.toLowerCase());
    for (const attribute of Array.from(element.attributes)) {
      const name = attribute.name.toLowerCase();
      if (!ALLOWED_ATTRIBUTES.has(name) || name.startsWith('on')) continue;
      const attributeValue = name === 'src' ? resolveInlineImageUrl(attribute.value) : attribute.value;
      if ((name === 'href' || name === 'src') && !safeUrl(attributeValue, name)) continue;
      if (name === 'style') {
        const safeStyle = sanitizeStyleValue(attribute.value);
        if (safeStyle) clean.setAttribute('style', safeStyle);
        continue;
      }
      clean.setAttribute(name, attributeValue);
    }
    if (clean.tagName === 'A' && clean.getAttribute('target') === '_blank') clean.setAttribute('rel', 'noopener noreferrer');
    for (const child of Array.from(element.childNodes)) appendSafe(child, clean);
    parent.appendChild(clean);
  };

  for (const child of Array.from(documentFragment.body.childNodes)) appendSafe(child, output);
  return output.innerHTML;
}
